// React hook — wires a `<canvas>` ref to a `PatternManifest` and
// re-renders when the manifest changes. The canonical client-side path
// for the architecture documented in `manifest/types.ts` (no per-asset
// bake; render from manifest at display time).
//
// DPR-aware: when `width` and `height` are passed in CSS pixels, the
// canvas's bitmap is scaled to `devicePixelRatio` for sharp output on
// retina displays. The canvas's CSS size still matches the requested
// width/height.
//
// Surfaces loading / error state so consumers can render skeletons /
// fallbacks during the fetch + render cycle.
//
// Usage (basic):
//   const ref = useRef<HTMLCanvasElement | null>(null);
//   const { state } = usePatternCanvas(ref, manifest, { width: 320, height: 180 });
//   return <canvas ref={ref} />;

'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useState } from 'react';
import { type ManifestFallbackEvent, renderManifest } from '../manifest/render';
import type { PatternManifest } from '../manifest/types';
export type PatternCanvasState = 'idle' | 'loading' | 'rendered' | 'error';
export interface UsePatternCanvasOptions {
  /** CSS pixel width. If omitted, uses the canvas's existing `width` attribute. */
  readonly width?: number;
  /** CSS pixel height. If omitted, uses the canvas's existing `height` attribute. */
  readonly height?: number;
  /** Override `devicePixelRatio` for the bitmap. Defaults to `window.devicePixelRatio`. */
  readonly dpr?: number;
  /** Called if `renderManifest` rejects (image fetch failure, etc.). */
  // ALLOW-UNKNOWN: generic error callback — caller narrows in their own handler
  readonly onError?: ((err: unknown) => void) | undefined;
  /** Called when a manifest reference falls back to a lib default. */
  readonly onFallback?: ((event: ManifestFallbackEvent) => void) | undefined;
}
export interface UsePatternCanvasResult {
  /**
   * Render lifecycle:
   *   - `idle`     — no manifest provided yet
   *   - `loading`  — fetch / sample / render in flight
   *   - `rendered` — paint complete
   *   - `error`    — terminal; `error` field carries the cause
   */
  readonly state: PatternCanvasState;
  // ALLOW-UNKNOWN: generic error state — caller narrows when displaying
  readonly error: unknown | null;
}
interface CompletedRender {
  readonly manifest: PatternManifest;
  readonly width: number | undefined;
  readonly height: number | undefined;
  readonly dprOverride: number | undefined;
  readonly onFallback: UsePatternCanvasOptions['onFallback'];
  readonly state: 'rendered' | 'error';
  // ALLOW-UNKNOWN: renderManifest can reject with an arbitrary browser or renderer error
  readonly error: unknown | null;
}
function getDpr(override: number | undefined): number {
  if (override !== undefined) return override;
  if (typeof window === 'undefined') return 1;
  return window.devicePixelRatio || 1;
}
export function usePatternCanvas(canvasRef, manifest, t0) {
  const $ = _c(16);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    width,
    height,
    dpr: dprOverride,
    onError,
    onFallback
  } = options;
  const [completed, setCompleted] = useState(null);
  let t2;
  let t3;
  if ($[2] !== canvasRef || $[3] !== dprOverride || $[4] !== height || $[5] !== manifest || $[6] !== onError || $[7] !== onFallback || $[8] !== width) {
    t2 = () => {
      if (manifest === null) {
        return;
      }
      const canvas = canvasRef.current;
      if (canvas === null) {
        return;
      }
      let cancelled = false;
      renderPatternCanvas(canvas, manifest, width, height, dprOverride, onFallback).then(() => {
        if (cancelled) {
          return;
        }
        setCompleted({
          manifest,
          width,
          height,
          dprOverride,
          onFallback,
          state: "rendered",
          error: null
        });
      }).catch(err => {
        if (cancelled) {
          return;
        }
        setCompleted({
          manifest,
          width,
          height,
          dprOverride,
          onFallback,
          state: "error",
          error: err
        });
        onError?.(err);
      });
      return () => {
        cancelled = true;
      };
    };
    t3 = [canvasRef, manifest, width, height, dprOverride, onError, onFallback];
    $[2] = canvasRef;
    $[3] = dprOverride;
    $[4] = height;
    $[5] = manifest;
    $[6] = onError;
    $[7] = onFallback;
    $[8] = width;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  if (manifest === null) {
    let t4;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        state: "idle",
        error: null
      };
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    return t4;
  }
  if (completed?.manifest === manifest && completed.width === width && completed.height === height && completed.dprOverride === dprOverride && completed.onFallback === onFallback) {
    let t4;
    if ($[12] !== completed.error || $[13] !== completed.state) {
      t4 = {
        state: completed.state,
        error: completed.error
      };
      $[12] = completed.error;
      $[13] = completed.state;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    return t4;
  }
  let t4;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      state: "loading",
      error: null
    };
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
async function renderPatternCanvas(canvas: HTMLCanvasElement, manifest: PatternManifest, width: number | undefined, height: number | undefined, dprOverride: number | undefined, onFallback: UsePatternCanvasOptions['onFallback']): Promise<void> {
  const cssWidth = width ?? canvas.width;
  const cssHeight = height ?? canvas.height;
  const dpr = getDpr(dprOverride);
  canvas.width = Math.max(1, Math.round(cssWidth * dpr));
  canvas.height = Math.max(1, Math.round(cssHeight * dpr));
  if (width !== undefined) canvas.style.width = `${cssWidth}px`;
  if (height !== undefined) canvas.style.height = `${cssHeight}px`;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('pattern-studio: could not acquire 2D context');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const renderOptions = onFallback !== undefined ? {
    onFallback
  } : {};
  await renderManifest(ctx, cssWidth, cssHeight, manifest, renderOptions);
}